import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { ROUTES } from '../lib/constants'; 

export default function HowItWorks() {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if the user is explicitly on the dedicated "How It Works" route
  const isDedicatedPage = location.pathname === ROUTES.HOW_IT_WORKS;

  const steps = [
    { 
      number: "1", 
      icon: "📄", 
      title: "Upload & Scan", 
      description: "Snap a photo of your textbook or upload a PDF. Our AI instantly reads the text, diagrams, and formulas." 
    },
    { 
      number: "2", 
      icon: "🧠", 
      title: "AI Simplification", 
      description: "Choose your level of clarity. OmniClarix strips away the jargon and gives you the core concepts in simple English." 
    },
    { 
      number: "3", 
      icon: "✅", 
      title: "Master the Topic", 
      description: "Generate instant flashcards and practice quizzes based on your material to lock in your knowledge for exams." 
    }
  ];

  return (
    <section id="how-it-works-section" className="scroll-mt-20 py-12">
      {/* Header Section */}
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-extrabold text-[#4A3728] mb-6 tracking-tight">
          How <span className="text-[#B08968]">OmniClarix</span> Works
        </h2>
        <p className="text-gray-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed px-4">
          We've combined cutting-edge AI with proven study techniques to help Nigerian students excel in record time.
        </p>
      </div>

      {/* Steps Grid */}
      <div className="grid gap-6 md:grid-cols-3 mb-20 px-4">
        {steps.map((step) => (
          <div 
            key={step.number} 
            className="group relative p-10 bg-white border border-gray-100 rounded-[2rem] shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
          >
            {/* The Large Background Ghost Number */}
            <span className="absolute top-4 left-6 text-[120px] font-black text-gray-50/80 transition-all duration-700 group-hover:text-gray-100 group-hover:scale-110 select-none pointer-events-none">
              {step.number}
            </span>

            <div className="relative z-10">
              {/* Icon / 3D Emoji */}
              <div className="text-5xl mb-8 flex justify-start">
                <span className="filter drop-shadow-md transform transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                  {step.icon}
                </span>
              </div>
              
              <h3 className="text-2xl font-bold text-[#4A3728] mb-4">
                {step.title}
              </h3>
              
              <p className="text-gray-500 text-sm md:text-[15px] leading-relaxed">
                {step.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* CTA Footer Card */}
      <div className="px-4">
        <div className="max-w-5xl mx-auto bg-gradient-to-r from-[#8D7566] to-[#735D50] rounded-[2.5rem] p-10 md:p-16 text-center shadow-2xl shadow-brown-200/50 flex flex-col items-center">
          <h3 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Ready to simplify your studies?
          </h3>
          <p className="text-cream-50/80 mb-10 text-sm md:text-lg">
            Join thousands of students turning complex books into easy A's.
          </p>
          
          <button 
            onClick={() => navigate(ROUTES.UPLOAD)}
            className="bg-white text-[#735D50] font-bold py-4 px-10 rounded-full hover:bg-gray-50 transition-all active:scale-95 shadow-lg text-lg mb-6"
          >
            Get Started for Free
          </button>

          {/* Subdued Back to Dashboard Anchor - Only displays on the standalone page */}
          {isDedicatedPage && (
            <button
              onClick={() => navigate(ROUTES.DASHBOARD)}
              className="flex items-center space-x-2 text-cream-100/70 hover:text-white transition-colors text-sm font-medium border-b border-transparent hover:border-white/40 pb-0.5"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Dashboard</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}